"use client";

import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

type MarkdownRendererProps = {
  content: string;
  className?: string;
};

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  // Convert highlight syntax (==text==) to HTML <mark> elements
  // This ensures it will work with rehype-raw
  const contentWithHighlights = content.replace(
    /==([^=]+)==/g, 
    (_, text) => `<mark class="highlight">${text}</mark>`
  );
  
  // Convert color syntax to HTML spans
  const contentWithColors = contentWithHighlights
    .replace(/\{a\}([^{}]+)\{\/a\}/g, '<span class="text-accent">$1</span>')
    .replace(/\{v\}([^{}]+)\{\/v\}/g, '<span class="text-violet">$1</span>')
    .replace(/\{g\}([^{}]+)\{\/g\}/g, '<span class="text-green">$1</span>');
  
  return (
    <div className={cn("container p-0 markdown-content text-base/6", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Override default element styling
          p: ({ children }) => (
            <p className="mb-4 last:mb-0">{children}</p>
          ),
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold mt-6 mb-4">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-bold mt-6 mb-3">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-bold mt-5 mb-2">{children}</h3>
          ),
          ul: ({ children }) => (
            <ul className="list-disc ml-6 mb-4">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal ml-6 mb-4">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="mb-1">{children}</li>
          ),
          a: ({ href, children }) => (
            <a 
              href={href} 
              className="text-indigo-700 underline underline-offset-4 font-semibold dark:text-indigo-400"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-300 dark:border-gray-700 pl-4 py-1 my-4 italic">
              {children}
            </blockquote>
          ),
          code: ({ children }) => (
            <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded font-mono text-sm">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-md overflow-x-auto mb-4 font-mono text-sm">
              {children}
            </pre>
          ),
          // We still need this in case the markdown parser creates mark elements directly
          mark: ({ children }) => (
            <mark className="highlight">
              {children}
            </mark>
          ),
        }}
      >
        {contentWithColors}
      </ReactMarkdown>
    </div>
  );
}